import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { CustomerEditComponent } from "@app/customer/components/customer-edit/customer-edit.component";
import { Customer } from "@app/customer/model/customer";
import { Entity } from "@app/entity/model/entity";
import { EmergencyContact, Municipality, Project } from "@app/project/model";
import { EmergencyContactDispatchers } from "@app/project/store/dispatchers/emergency-contact.dispatchers";
import { MunicipalityDispatchers } from "@app/project/store/dispatchers/municipality.dispatchers";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { filter, map, Subject, take, takeUntil } from "rxjs";
import { AddContactComponent } from "../emergency-contact/add-contact/add-contact.component";
import { EditMunicipalityComponent } from "../emergency-contact/edit-municipality/edit-municipality.component";

@Component({
    selector: "app-contact-info-step",
    templateUrl: "./contact-info-step.component.html",
    styleUrls: ["./contact-info-step.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactInfoStepComponent implements OnInit, OnDestroy {
    @ViewChild("addManucipalityTmpl", { static: true }) addManucipalityTmpl: TemplateRef<EditMunicipalityComponent>;
    @ViewChild("addContactTmpl", { static: true }) addContactTmpl: TemplateRef<AddContactComponent>;
    @ViewChild("userEditTmpl", { static: true, read: TemplateRef }) userEditTmpl: TemplateRef<CustomerEditComponent>;
    @ViewChild("userAddTmpl", { static: true }) userAddTmpl: TemplateRef<CustomerEditComponent>;

    @Input() project: Project;
    @Input() customer: Customer = null;
    @Input() entity: Entity = null;
    municipality: Municipality;

    @Output() navigateEditUser = new EventEmitter();

    private contactList: EmergencyContact[] = [];
    private readonly unsubscribe$ = new Subject();

    constructor(
        private dialog: MatDialog,
        private readonly municipalityDispatchers: MunicipalityDispatchers,
        private readonly emergencyContactDispatchers: EmergencyContactDispatchers
    ) {}

    ngOnInit(): void {
        this.initializeData();
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    openManucipalityForm() {
        this.dialog.open(this.addManucipalityTmpl, {
            width: "400px",
        });
    }

    openAddContactForm() {
        this.dialog.open(this.addContactTmpl, {
            width: "400px",
        });
    }

    closeDialog() {
        this.dialog.closeAll();
    }

    openCustomerForm(isEditing = false) {
        if (isEditing) {
            this.dialog.open(this.userEditTmpl);
            return;
        }

        this.dialog.open(this.userAddTmpl);
    }

    editUser() {
        this.navigateEditUser.emit();
    }

    upsertMunicipality(municipality: Municipality) {
        const eventEmitter = new EventEmitter();
        eventEmitter.pipe(take(1)).subscribe(action => {
            this.municipalityDispatchers.dispatch(action);
            this.municipalityDispatchers.clear();
            this.closeDialog();
        });

        this.municipalityDispatchers.upsert(
            {
                ...municipality,
                projectId: this.project.id,
            },
            eventEmitter
        );
    }

    saveContact(contact: EmergencyContact) {
        const eventEmitter = new EventEmitter();
        eventEmitter.pipe(take(1)).subscribe(action => {
            this.emergencyContactDispatchers.dispatch(action);
            this.municipalityDispatchers.clear();
            this.closeDialog();
        });
        let contactData: EmergencyContact = null;
        if (contact?.id) {
            contactData = contact;
        } else {
            const orderList = this.contactList.map(item => +item.order);
            const newOrder = Math.max(0, ...orderList) + 10;
            contactData = {
                ...contact,
                order: newOrder,
            };
        }

        this.emergencyContactDispatchers.upsertEmergencyContact(
            {
                ...contactData,
                projectId: this.project.id,
            },
            eventEmitter
        );
    }

    private initializeData() {
        this.municipalityDispatchers
            .load(this.project.id)
            .pipe(
                filter((state: ApiResultState<Municipality>) => state?.callState === LoadingState.loaded),
                takeUntil(this.unsubscribe$),
                map(municipalityState => municipalityState.result)
            )
            .subscribe(municipality => (this.municipality = municipality));
        this.emergencyContactDispatchers
            .getEmergencyContacts(this.project.id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(contactList => {
                this.contactList = [...contactList];
            });
    }
}
